import { currencyUtils, PaylinkResponse } from 'skipify-types';

import { phone as phoneFormatter, formatCurrency } from './formatters';
import { PaylinkCustomerInfoResponse } from '../store/modules/paylink/types';
import type { MerchantAccess } from '../store/modules/merchant/types';

const merchantDomainErrorMessage = (domain: string): string => {
  // Check if domain has a valid protocol (http:// or https://)
  const hasValidProtocol = domain.startsWith('https://') || domain.startsWith('http://');
  if (!hasValidProtocol) {
    return `Unable to add domain "${domain}", please provide a valid protocol`;
  }

  try {
    const url = new URL(domain);

    // Check for valid TLD
    if (!url.hostname.includes('.')) {
      return `Unable to add domain, "${domain}" must have a valid top-level domain (e.g., .com)`;
    }

    // Check for path presence
    if (domain.endsWith('/')) {
      return `Unable to add domain, "${domain}" cannot end with a trailing slash`;
    }

    // URL is valid
    return '';
  } catch {
    return `Unable to add domain "${domain}"`;
  }
};

/** Stand-in for the unknown storefront origin (`.invalid` can never be a real host). */
const HANDOFF_URL_BASE = 'https://storefront.invalid';
const HANDOFF_URL_ORIGIN = new URL(HANDOFF_URL_BASE).origin;

/**
 * Root-relative path such as `/checkout` or `/pages/checkout?fast=1`. Resolution against a
 * placeholder origin rejects protocol-relative and other cross-origin forms.
 */
const isRootRelativePath = (value: string): boolean => {
  if (!value.startsWith('/')) {
    return false;
  }

  try {
    return new URL(value, HANDOFF_URL_BASE).origin === HANDOFF_URL_ORIGIN;
  } catch {
    return false;
  }
};

/**
 * Checkout handoff destinations must be a root-relative path (the SDK only navigates
 * same-origin). Absolute http(s) URLs are not accepted.
 */
const isValidHandoffUrl = (value: string): boolean => {
  const trimmed = value.trim();
  return isRootRelativePath(trimmed);
};

const isAbsoluteHttpUrl = (value: string): boolean => {
  try {
    const protocol = new URL(value).protocol.toLowerCase();
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
};

/** Order-complete destination: root-relative path or absolute http(s) URL. */
const isValidOrderCompleteUrl = (value: string): boolean => {
  const trimmed = value.trim();
  return isRootRelativePath(trimmed) || isAbsoluteHttpUrl(trimmed);
};

const checkIsURLMatching = (pathOne: string, pathTwo: string) =>
  pathOne.toLocaleLowerCase() === pathTwo.toLocaleLowerCase();

const checkIsSubRoute = (baseRoute: string, route: string) =>
  route.toLocaleLowerCase().search(baseRoute.toLocaleLowerCase()) === 0;

/**
 * Dynamic appBar height
 * @param {boolean} isMobile Used to determine if the app is being viewed on a mobile device
 * @return {number} AppBar height
 */
export const getAppBarHeight = (isMobile = false): number => (isMobile ? 210 : 70);

type ErrorResponse = {
  status: number;
  data: {
    statusCode: number;
    message: string;
  };
};

type GenericServerError = {
  status: number;
  data: {
    error: string;
  };
};

export const isServerError = (err: unknown): err is ErrorResponse =>
  typeof err === 'object' &&
  err !== null &&
  'data' in err &&
  typeof err.data === 'object' &&
  err.data !== null &&
  'statusCode' in err.data &&
  'message' in err.data;

export const isGenericServerError = (err: unknown): err is GenericServerError =>
  typeof err === 'object' &&
  err !== null &&
  'data' in err &&
  typeof err.data === 'object' &&
  err.data !== null &&
  'error' in err.data;

function formatApiErrorMessage(message: unknown): string | undefined {
  if (typeof message === 'string') {
    const trimmed = message.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }
  if (Array.isArray(message)) {
    const parts = message
      .filter((part): part is string => typeof part === 'string')
      .map((part) => part.trim())
      .filter((part) => part.length > 0);
    return parts.length > 0 ? parts.join(' ') : undefined;
  }
  return undefined;
}

/** User-safe API error text from RTK Query / MMS 4xx responses and rethrown Errors. */
export function getClientFacingApiErrorMessage(err: unknown): string | undefined {
  if (typeof err === 'object' && err !== null && 'status' in err && 'data' in err) {
    const { status, data } = err as { status: unknown; data: unknown };
    if (
      typeof status === 'number' &&
      status >= 400 &&
      status < 500 &&
      typeof data === 'object' &&
      data !== null &&
      'message' in data
    ) {
      const message = formatApiErrorMessage((data as { message: unknown }).message);
      if (message) {
        return message;
      }
    }
  }

  if (err instanceof Error && err.message.trim().length > 0) {
    return err.message;
  }

  return undefined;
}

/**
 * Returns the appropriate user info to display with the paylink's details
 * @param paylink The paylink to get the user info from
 * @returns The user info to display and the user info type (name, email, or phone)
 */
export const getDisplayedPaylinkUserInfo = ({
  customer,
  sendEmail,
}: PaylinkResponse): { userInfo: string; userInfoType: 'name' | 'email' | 'phone' } => {
  const { firstName, lastName, email, phone } = customer;
  if (firstName && lastName) {
    return { userInfo: `${firstName} ${lastName}`, userInfoType: 'name' };
  }
  if (sendEmail) {
    return { userInfo: email as string, userInfoType: 'email' };
  }

  return { userInfo: phoneFormatter(phone as string), userInfoType: 'phone' };
};

/**
 * Returns the amount to display based on the fields available
 * @param paylink The paylink to get the user info from
 * @returns The amount to display
 */
export const getDisplayedPaylinkAmount = ({
  approvedAmount,
  orderTotal,
  total,
  currencyCode,
}: PaylinkResponse): string => {
  const totalMinor = orderTotal ?? total;
  const totalFormatted = formatCurrency(currencyUtils.minorToMajor(totalMinor), currencyCode);

  if (approvedAmount != null && approvedAmount < totalMinor) {
    const paidFormatted = formatCurrency(currencyUtils.minorToMajor(approvedAmount), currencyCode);
    return `${paidFormatted}/${totalFormatted}`;
  }

  return totalFormatted;
};

export const debounce = (func: (...args: any[]) => void | Promise<void>, wait: number) => {
  let timeout: ReturnType<typeof setTimeout>;

  return (...args: any[]) => {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

export const isPaylinkCustomerInfo = (value: unknown): value is PaylinkCustomerInfoResponse => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const customerInfo = value as PaylinkCustomerInfoResponse;
  return customerInfo.email !== undefined || customerInfo.phone !== undefined;
};

export const isValidUUIDv4 = (uuid: string): boolean =>
  /^[0-9A-F]{8}-[0-9A-F]{4}-[4][0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i.test(uuid);

export const sanitizeNumericString = (phone?: string | null) => phone?.replaceAll(/\D/g, '') ?? '';

export const getDisplayedMITCustomerInfo = (customer: {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string | null;
}): string => {
  const { firstName, lastName, email, phone } = customer;

  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  }

  if (email) {
    return email;
  }

  if (phone) {
    return phoneFormatter(phone);
  }

  return 'N/A';
};

/** Paginated / legacy list APIs may return snake_case ids. */
export type MerchantAccessApiRow = MerchantAccess & { merchant_id?: string };

export const merchantAccessIdFromRow = (row: MerchantAccessApiRow): string =>
  (row.merchantId ?? row.merchant_id ?? '').trim();

/**
 * Builds a URL with the merchantId in the search params
 * @param path The path to build the URL from, including the search params
 * @param merchantId The merchantId to add to the URL - Will be ignored if the merchantId is already in the search params
 * @returns The URL with the merchantId in the search params
 */
export const buildUrlWithMerchantId = (path: string, merchantId: string) => {
  const [route, searchParams] = path.split('?');
  const params = new URLSearchParams(searchParams ?? '');
  const trimmed = merchantId?.trim() ?? '';
  if (!trimmed) {
    params.delete('merchantId');
    const qs = params.toString();
    return qs ? `${route}?${qs}` : route;
  }
  if (!params.has('merchantId')) {
    params.set('merchantId', trimmed);
  }
  return `${route}?${params.toString()}`;
};

export {
  checkIsSubRoute,
  checkIsURLMatching,
  isValidHandoffUrl,
  isValidOrderCompleteUrl,
  merchantDomainErrorMessage,
};
